import { InlineInput } from '@/components/InlineInput/InlineInput'
import { useEffect, useState } from 'react'

import StandaloneMessageService from '@/app-domains/messages/draft/writing.service'
import validator from 'validator'

import './styles.scoped.sass'
import ToastService from '@/components/Toast/Toast.service'
import store from '@/core/store'
import sessionService from '@/app-domains/user/session/session.service'
import contactsService from '@/app-domains/contacts/contacts.service'
import NameSlugPlaceholder from '@/pages/HomePage/FeedContent/Thread/CardMainSection/MainSectionHeader/NameSlugPlaceholder'
import useRenderEvent from '@/core/hooks/useRenderEvent'
import { manageArrowKeysDropdown } from '@/helpers/arrow-selector-dropdown'
import modalService from '@/services/modal.service'
import SelectContactEmail from './components/SelectContactEmail/SelectContactEmail'

export default function MessageTo (props) {
  const [inputDisabled, setInputDisabled] = useState(false)
  const [toInputValue, setToInputValue] = useState('')
  const [ccInputValue, setCcInputValue] = useState('')
  const [bccInputValue, setBccInputValue] = useState('')
  const [isToInputDirty, setIsToInputDirty] = useState(false)
  const [fromBoxRelatedContacts, setFromBoxRelatedContacts] = useState([])
  const [isCCActive, setIsCCActive] = useState(false)
  const [isBCCActive, setIsBCCActive] = useState(false)

  useRenderEvent(['SHOW_CC_INPUT'], () => setIsCCActive(true))
  useRenderEvent(['SHOW_BCC_INPUT'], () => setIsBCCActive(true))

  function doMailAndThreadShareTheSameParticipants (threadParticipants, incomingMailParticipants) {
    // Remove own user box / domain as a participant
    threadParticipants = threadParticipants.filter((participant) => !participant.split('@')[1].includes(sessionService.getMe().me.defaultdomain))
    return areAllIncomingMailParticipantsIncludedInThread(
      threadParticipants,
      incomingMailParticipants,
    ) && threadParticipants.length === incomingMailParticipants.length
  }

  function areAllIncomingMailParticipantsIncludedInThread (
    threadParticipants,
    incomingMailParticipants,
  ) {
    threadParticipants = threadParticipants.map(
      (threadParticipant) => threadParticipant.toLowerCase(),
    )
    return incomingMailParticipants.filter(
      (participant) => threadParticipants.indexOf(participant.toLowerCase()) !== -1,
    ).length === incomingMailParticipants.length
  }

  async function handleAddTo (toValue) {
    setToInputValue('')
    setInputDisabled(
      StandaloneMessageService.checkIfMaxAddressesWillBeReached()
    )
    if (!validator.isEmail(toValue)) {
      return ToastService.show({
        type: 'error',
        title: 'Error',
        message: 'Invalid mail address',
      })
    }
    await StandaloneMessageService.addToAddress(toValue)
  }

  async function handleAddCc (toValue) {
    setCcInputValue('')
    setInputDisabled(
      StandaloneMessageService.checkIfMaxAddressesWillBeReached()
    )
    if (!validator.isEmail(toValue)) {
      return ToastService.show({
        type: 'error',
        title: 'Error',
        message: 'Invalid mail address',
      })
    }
    await StandaloneMessageService.addToAddress(toValue, 'ccs')
  }

  async function handleAddBcc (toValue) {
    setBccInputValue('')
    setInputDisabled(
      StandaloneMessageService.checkIfMaxAddressesWillBeReached()
    )
    if (!validator.isEmail(toValue)) {
      return ToastService.show({
        type: 'error',
        title: 'Error',
        message: 'Invalid mail address',
      })
    }
    await StandaloneMessageService.addToAddress(toValue, 'bccs')
  }

  async function onKeyDownHandler (e) {
    const { keyCode } = e

    manageArrowKeysDropdown(keyCode, '.matching-contacts-body > ul', 'contact-option-selected-with-arrow')

    if (e.keyCode === 13 || e.keyCode === 32) {
      e.preventDefault()
      const selectedOption = document?.querySelector('.contact-option-selected-with-arrow')
      if (selectedOption) {
        selectedOption.dispatchEvent(new Event('mousedown', {
          view: window,
          bubbles: true,
          cancelable: true,
          buttons: 1
        }))
        return
      }
      if (e.target.value) {
        await handleAddTo(e.target.value.trim())
      }
    }
  }

  async function onCcKeyDownHandler (e) {
    if (e.keyCode === 13 && e.target.value) {
      await handleAddCc(e.target.value)
    }
  }

  async function onBccKeyDownHandler (e) {
    if (e.keyCode === 13 && e.target.value) {
      await handleAddBcc(e.target.value)
    }
  }

  function onCcChangeHandler (e) {
    if (!isToInputDirty) {
      setIsToInputDirty(true)
      StandaloneMessageService.setTosDirty(true)
    }
    setToInputValue('')
    setBccInputValue('')
    const cc = e.target.value?.toLowerCase()
    setCcInputValue(cc.replace(/ /g, ''))
  }

  function onBccChangeHandler (e) {
    if (!isToInputDirty) {
      setIsToInputDirty(true)
      StandaloneMessageService.setTosDirty(true)
    }
    setCcInputValue('')
    setToInputValue('')
    const bcc = e.target.value?.toLowerCase()
    setBccInputValue(bcc.replace(/ /g, ''))
  }

  function getMatchingContactsFor (tosType) {
    let inputValue
    if (tosType === 'tos') {
      inputValue = toInputValue
    } else if (tosType === 'ccs') {
      inputValue = ccInputValue
    } else if (tosType === 'bccs') {
      inputValue = bccInputValue
    }
    if (!isToInputDirty || inputValue.length <= 1) {
      return []
    }
    const matchingContacts = contactsService.findAllContactsMatching(inputValue)
    const alreadyPickedMails = [...(props.draft?.tos || []), ...(props.draft?.ccs || []), ...(props.draft?.bccs || [])]

    // Avoid showing duplicated contacts
    if (alreadyPickedMails && alreadyPickedMails.length > 0) {
      return matchingContacts.filter((contact) => {
        return !contact.emails.find((mail) => alreadyPickedMails.indexOf(mail) > -1)
      })
    }
    return matchingContacts
  }

  function onChangeHandler (e) {
    if (!isToInputDirty) {
      setIsToInputDirty(true)
      StandaloneMessageService.setTosDirty(true)
    }
    setCcInputValue('')
    setBccInputValue('')
    const to = e.target.value?.toLowerCase()
    setToInputValue(to.replace(/ /g, ''))
  }

  function validateContentAndInjectTo () {
    if (validator.isEmail(toInputValue)) { handleAddTo(toInputValue) }
    setToInputValue('')
  }

  function validateContentAndInjectCC () {
    if (validator.isEmail(ccInputValue)) { handleAddCc(ccInputValue) }
    setCcInputValue('')
  }

  function validateContentAndInjectBCC () {
    if (validator.isEmail(bccInputValue)) { handleAddBcc(bccInputValue) }
    setBccInputValue('')
  }

  // Only drop the contact from the picker once the address actually landed on
  // the draft. It used to remove unconditionally, so a failed add made the
  // contact vanish from the list without ever becoming a recipient -- the click
  // looked like it worked and nothing happened.
  async function addSingleBoxContact (email) {
    const added = await StandaloneMessageService.addToAddress(email, 'tos')
    if (added) {
      setFromBoxRelatedContacts(prev => prev.filter(p => p.email !== email))
    }
  }

  useEffect(() => {
    store.on('DRAFT_MESSAGE:RELATED_CONTACTS_FROM_BOX', (fromList) => {
      // Each entry is { email, name }. Filter out no-reply senders.
      const cleaned = (fromList || []).filter(({ email }) => {
        return email && !email.includes('no-reply') && !email.includes('noreply')
      })
      setFromBoxRelatedContacts(cleaned)
    })
  }, [])

  function addToFromContactChoice (contact) {
    setToInputValue('')
    const mainEmailFromContact = contact.metadata.primaryEmail || contact.emails[0]
    StandaloneMessageService.addToAddress(mainEmailFromContact, 'tos')
  }

  function addCCFromContactChoice (contact) {
    setCcInputValue('')
    const mainEmailFromContact = contact.metadata.primaryEmail || contact.emails[0]
    StandaloneMessageService.addToAddress(mainEmailFromContact, 'ccs')
  }

  function addBCCFromContactChoice (contact) {
    setBccInputValue('')
    const mainEmailFromContact = contact.metadata.primaryEmail || contact.emails[0]
    StandaloneMessageService.addToAddress(mainEmailFromContact, 'bccs')
  }

  /**
   * ONMOUSEDOWN is needed in the list of the matching contacts since the blur happens after and the section disappears
   * since in blur we are setting the input value to '' and therefore the contacts don't render
   */

  return (<>
    <div className='to-section'>
      {fromBoxRelatedContacts.length > 0 &&
      <div className='box-contacts-picker'>
        <div className='box-contacts-picker-header'>
          {fromBoxRelatedContacts.length === 1 ? '1 contact' : `${fromBoxRelatedContacts.length} contacts`} linked to this box
        </div>
        <ul>
          {fromBoxRelatedContacts.map(({ email, name: apiName }) => {
            const contact = contactsService.findContactByEmail(email)
            const contactName = contact ? contactsService.buildContactFullname(contact) : null
            const name = contactName || apiName || null
            return (
              <li key={email} className='box-contact-row' onClick={() => addSingleBoxContact(email)}>
                <div className='box-contact-avatar'>
                  {contact?.contactimageuri
                    ? <img src={contact.contactimageuri} />
                    : <span className='box-contact-initial'>{(name || email).charAt(0).toUpperCase()}</span>}
                </div>
                <div className='box-contact-info'>
                  {name
                    ? <span className='box-contact-line'>
                      <span className='box-contact-name'>{name}</span>
                      <span className='box-contact-email'>&lt;{email}&gt;</span>
                    </span>
                    : <span className='box-contact-email'>{email}</span>}
                </div>
              </li>
            )
          })}
        </ul>
      </div>}
      { props.thread && props.draft && props.draft.type === 'reply' && !doMailAndThreadShareTheSameParticipants(props.thread.participants, props.draft?.tos || []) && <div className='new-contact-reply-alert'>Adding new contacts into a reply will create a new thread</div>}
      <div className='to-section-input'>
        <span className='to-section-input-text'>To</span> <InlineInput
        disabled={inputDisabled}
        autoComplete={true}
        isToSection={true}
        draft={props.draft}
        tos={props.draft?.tos}
        value={toInputValue}
        placeholder='To ...'
        onInput={onChangeHandler}
        onKeyDown={onKeyDownHandler}
        onBlur={validateContentAndInjectTo}
        recipientType='tos'
      />
        <div className='tos-options'>
          { !isCCActive && <div className="option" onClick={() => { store.commit('SHOW_CC_INPUT') }}>Cc</div>}
          { !isBCCActive && <div className="option" onClick={() => { store.commit('SHOW_BCC_INPUT') }}>Bcc</div>}
        </div>
        {getMatchingContactsFor('tos').length > 0 && <div className='matching-contacts'>
          <div className='matching-contacts-body'>
            <ul>
              {getMatchingContactsFor('tos').map(contact => <li key={contact.id} className='to-item' onMouseDown={() => addToFromContactChoice(contact)}>
                <div className='left-part'>
                  <div className='contact-img'>{ contact?.contactimageuri
                    ? <img className='contact-image-img' src={contact.contactimageuri}/>
                    : <NameSlugPlaceholder name={contactsService.buildContactFullname(contact) || '-'}/>}</div>
                  <div className='contact-info'>
                    <div className='contact-name'>
                      {contactsService.buildContactFullname(contact) || '-'}
                    </div>
                    <div className='mail-info'>
                      <span className='mail-address'>{contact.metadata.primaryEmail || contact.emails[0]}</span>
                      <span className='primary-text'>primary</span>
                      {contact.emails.length > 1 && <span className='other-emails-number'>+{contact.emails.length - 1}</span>}
                    </div>
                  </div>
                </div>
                <div className='other-emails-div'>
                  <span className='other-emails-button' onMouseDown={(e) => {
                    e.stopPropagation()
                    modalService.open(<SelectContactEmail contact={contact} field='tos' />)
                  }}>Choose other emails</span>
                </div>
              </li>)}
            </ul>
          </div>
        </div>}
      </div>
      { isCCActive && <div className='to-section-input'>
        <span className='to-section-input-text'>CC</span> <InlineInput
        disabled={inputDisabled}
        autoComplete={true}
        value={ccInputValue}
        draft={props.draft}
        onInput={onCcChangeHandler}
        onKeyDown={onCcKeyDownHandler}
        onBlur={validateContentAndInjectCC}
        placeholder='CCs ...'
        recipientType='ccs'
      /> {getMatchingContactsFor('ccs').length > 0 && <div className='matching-contacts'>
        <div className='matching-contacts-body'>
          <ul>
            {getMatchingContactsFor('ccs').map(contact => <li key={contact.id} onMouseDown={() => addCCFromContactChoice(contact)}>
              <div className='left-part'>
                <div className='contact-img'>{ contact?.contactimageuri
                  ? <img className='contact-image-img' src={contact.contactimageuri}/>
                  : <NameSlugPlaceholder name={contactsService.buildContactFullname(contact) || '-'}/>}</div>
                <div className='contact-info'>
                  <div className='contact-name'>
                    {contactsService.buildContactFullname(contact) || '-'}
                  </div>
                  <div className='mail-info'>
                    <span className='mail-address'>{contact.metadata.primaryEmail || contact.emails[0]}</span>
                    <span className='primary-text'>primary</span>
                    {contact.emails.length > 1 && <span className='other-emails-number'>+{contact.emails.length - 1}</span>}
                  </div>
                </div>
              </div>
              <div className='other-emails-div'>
                <span className='other-emails-button' onMouseDown={(e) => {
                  e.stopPropagation()
                  modalService.open(<SelectContactEmail contact={contact} field='ccs'/>)
                }}>Choose other emails</span>
              </div>
            </li>)}
          </ul>
        </div>
      </div>}
      </div>}
      { isBCCActive && <div className='to-section-input'> <span className='to-section-input-text'>BCC</span> <InlineInput
        disabled={inputDisabled}
        autoComplete={true}
        draft={props.draft}
        value={bccInputValue}
        onInput={onBccChangeHandler}
        onKeyDown={onBccKeyDownHandler}
        onBlur={validateContentAndInjectBCC}
        placeholder='BCCs ...'
        recipientType='bccs'
      />
        {getMatchingContactsFor('bccs').length > 0 && <div className='matching-contacts'>
          <div className='matching-contacts-body'>
            <ul>
              {getMatchingContactsFor('bccs').map(contact => <li key={contact.id} onMouseDown={() => addBCCFromContactChoice(contact)}>
                <div className='left-part'>
                  <div className='contact-img'>{ contact?.contactimageuri
                    ? <img className='contact-image-img' src={contact.contactimageuri}/>
                    : <NameSlugPlaceholder name={contactsService.buildContactFullname(contact) || '-'}/>}</div>
                  <div className='contact-info'>
                    <div className='contact-name'>
                      {contactsService.buildContactFullname(contact) || '-'}
                    </div>
                    <div className='mail-info'>
                      <span className='mail-address'>{contact.metadata.primaryEmail || contact.emails[0]}</span>
                      <span className='primary-text'>primary</span>
                      {contact.emails.length > 1 && <span className='other-emails-number'>+{contact.emails.length - 1}</span>}
                    </div>
                  </div>
                </div>
                <div className='other-emails-div'>
                  <span className='other-emails-button' onMouseDown={(e) => {
                    e.stopPropagation()
                    modalService.open(<SelectContactEmail contact={contact} field='bccs' />)
                  }}>Choose other emails</span>
                </div>
              </li>)}
            </ul>
          </div>
        </div>}</div>}
      
    </div>
  </>
  )
}
